import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './unicode-table.component.module.scss';

interface Block {
    name: string;
    start: number;
    end: number;
    note?: string;
}

const BLOCKS: Block[] = [
    { name: 'Basic Latin (ASCII)', start: 0x0020, end: 0x007E },
    { name: 'Latin-1 Supplement', start: 0x00A0, end: 0x00FF },
    { name: 'Latin Extended-A', start: 0x0100, end: 0x017F },
    { name: 'Latin Extended-B', start: 0x0180, end: 0x024F },
    { name: 'IPA Extensions', start: 0x0250, end: 0x02AF },
    { name: 'Greek and Coptic', start: 0x0370, end: 0x03FF },
    { name: 'Cyrillic', start: 0x0400, end: 0x04FF },
    { name: 'Hebrew', start: 0x0590, end: 0x05FF },
    { name: 'Arabic', start: 0x0600, end: 0x06FF },
    { name: 'Devanagari', start: 0x0900, end: 0x097F },
    { name: 'Thai', start: 0x0E00, end: 0x0E7F },
    { name: 'General Punctuation', start: 0x2000, end: 0x206F, note: 'em dash, en dash, smart quotes' },
    { name: 'Superscripts & Subscripts', start: 0x2070, end: 0x209F },
    { name: 'Currency Symbols', start: 0x20A0, end: 0x20CF, note: '€ ₹ ₿ etc.' },
    { name: 'Letterlike Symbols', start: 0x2100, end: 0x214F, note: '™ ℃ ℵ' },
    { name: 'Arrows', start: 0x2190, end: 0x21FF, note: '← → ↑ ↓ ⇒' },
    { name: 'Mathematical Operators', start: 0x2200, end: 0x22FF, note: '∀ ∃ ∈ ∑ ∫' },
    { name: 'Box Drawing', start: 0x2500, end: 0x257F },
    { name: 'Block Elements', start: 0x2580, end: 0x259F },
    { name: 'Geometric Shapes', start: 0x25A0, end: 0x25FF },
    { name: 'Miscellaneous Symbols', start: 0x2600, end: 0x26FF, note: '☀ ☎ ⚡ ♠' },
    { name: 'Dingbats', start: 0x2700, end: 0x27BF, note: '✓ ✦ ❤' },
    { name: 'CJK Unified Ideographs', start: 0x4E00, end: 0x4E5F, note: 'sample of larger 4E00–9FFF block' },
    { name: 'Hiragana', start: 0x3040, end: 0x309F },
    { name: 'Katakana', start: 0x30A0, end: 0x30FF },
    { name: 'Hangul Syllables', start: 0xAC00, end: 0xAC5F, note: 'sample of larger AC00–D7AF block' },
    { name: 'Emoticons', start: 0x1F600, end: 0x1F64F, note: '😀 🥰 😎' },
    { name: 'Misc. Symbols and Pictographs', start: 0x1F300, end: 0x1F37F, note: 'sample (1F300–1F5FF)' },
    { name: 'Transport and Map Symbols', start: 0x1F680, end: 0x1F6FF },
    { name: 'Mathematical Alphanumeric Symbols', start: 0x1D400, end: 0x1D45F, note: 'sample of larger 1D400–1D7FF block' },
];

interface State {
    blockIdx: number;
    cpInput: string;
    charInput: string;
    cpResult: { hex: string; dec: number; char: string; utf8: string; utf16: string; htmlDec: string; htmlHex: string; jsEsc: string } | null;
    cpError: string | null;
}

function utf8Bytes(cp: number): string {
    const buf: number[] = [];
    if (cp < 0x80) {
        buf.push(cp);
    } else if (cp < 0x800) {
        buf.push(0xC0 | (cp >> 6), 0x80 | (cp & 0x3F));
    } else if (cp < 0x10000) {
        buf.push(0xE0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3F), 0x80 | (cp & 0x3F));
    } else {
        buf.push(0xF0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3F), 0x80 | ((cp >> 6) & 0x3F), 0x80 | (cp & 0x3F));
    }
    return buf.map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(' ');
}

function utf16Units(cp: number): string {
    if (cp <= 0xFFFF) {
        return cp.toString(16).padStart(4, '0').toUpperCase();
    }
    const v = cp - 0x10000;
    const hi = 0xD800 + (v >> 10);
    const lo = 0xDC00 + (v & 0x3FF);
    return `${hi.toString(16).padStart(4, '0').toUpperCase()} ${lo.toString(16).padStart(4, '0').toUpperCase()}`;
}

function jsEscape(cp: number): string {
    if (cp <= 0xFFFF) return '\\u' + cp.toString(16).padStart(4, '0').toUpperCase();
    return '\\u{' + cp.toString(16).toUpperCase() + '}';
}

export default class UnicodeTableComponent extends Component<{ feature?: string }, State> {
    state: State = {
        blockIdx: 0,
        cpInput: 'U+1F600',
        charInput: '😀',
        cpResult: null,
        cpError: null,
    };

    componentDidMount() {
        this.lookupCp(this.state.cpInput);
    }

    private buildResult(cp: number) {
        return {
            hex: cp.toString(16).toUpperCase().padStart(4, '0'),
            dec: cp,
            char: String.fromCodePoint(cp),
            utf8: utf8Bytes(cp),
            utf16: utf16Units(cp),
            htmlDec: `&#${cp};`,
            htmlHex: `&#x${cp.toString(16).toUpperCase()};`,
            jsEsc: jsEscape(cp),
        };
    }

    lookupCp = (val: string) => {
        const cleaned = val.trim().replace(/^U\+/i, '').replace(/^0x/i, '');
        if (!cleaned) {
            this.setState({ cpInput: val, cpResult: null, cpError: null });
            return;
        }
        const cp = parseInt(cleaned, 16);
        if (Number.isNaN(cp) || cp < 0 || cp > 0x10FFFF) {
            this.setState({ cpInput: val, cpResult: null, cpError: 'Code point must be 0–10FFFF (hex).' });
            return;
        }
        this.setState({ cpInput: val, cpResult: this.buildResult(cp), cpError: null, charInput: String.fromCodePoint(cp) });
    };

    lookupChar = (val: string) => {
        if (!val) {
            this.setState({ charInput: val });
            return;
        }
        const cp = val.codePointAt(0);
        if (cp === undefined) {
            this.setState({ charInput: val });
            return;
        }
        const result = this.buildResult(cp);
        this.setState({
            charInput: val,
            cpInput: 'U+' + result.hex,
            cpResult: result,
            cpError: null,
        });
    };

    private get blockChars(): { cp: number; char: string }[] {
        const block = BLOCKS[this.state.blockIdx];
        const out: { cp: number; char: string }[] = [];
        for (let cp = block.start; cp <= block.end; cp++) {
            out.push({ cp, char: String.fromCodePoint(cp) });
        }
        return out;
    }

    render() {
        const { blockIdx, cpInput, charInput, cpResult, cpError } = this.state;
        const block = BLOCKS[blockIdx];
        return (
            <div className="row">
                <div className="col-12 col-lg-5">
                    <h3 className={styles.sectionTitle}>Code-point lookup</h3>
                    <label className={sharedStyles.label}>
                        Code point (hex, e.g. U+1F600)
                        <input className={`${styles.input} ${cpError ? styles.inputError : ''}`} type="text"
                            value={cpInput} onChange={e => this.lookupCp(e.target.value)} spellCheck={false} />
                    </label>
                    <label className={sharedStyles.label}>
                        Character
                        <input className={styles.input} type="text" value={charInput}
                            onChange={e => this.lookupChar(e.target.value)} maxLength={2} />
                    </label>
                    {cpError && <p className={styles.error} role="alert">{cpError}</p>}
                    {cpResult && (
                        <div className={styles.resultBox}>
                            <div className={styles.bigChar}>{cpResult.char}</div>
                            <div className={styles.fields}>
                                <div><span>Code point</span><code>U+{cpResult.hex}</code></div>
                                <div><span>Decimal</span><code>{cpResult.dec}</code></div>
                                <div><span>UTF-8 bytes</span><code>{cpResult.utf8}</code></div>
                                <div><span>UTF-16 units</span><code>{cpResult.utf16}</code></div>
                                <div><span>HTML entity (dec)</span><code>{cpResult.htmlDec}</code></div>
                                <div><span>HTML entity (hex)</span><code>{cpResult.htmlHex}</code></div>
                                <div><span>JS escape</span><code>{cpResult.jsEsc}</code></div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="col-12 col-lg-7">
                    <h3 className={styles.sectionTitle}>Block browser</h3>
                    <label className={sharedStyles.label}>
                        Block
                        <select className={styles.input} value={blockIdx}
                            onChange={e => this.setState({ blockIdx: parseInt(e.target.value, 10) })}>
                            {BLOCKS.map((b, i) => (
                                <option key={i} value={i}>
                                    {b.name} (U+{b.start.toString(16).toUpperCase().padStart(4, '0')}–U+{b.end.toString(16).toUpperCase().padStart(4, '0')})
                                </option>
                            ))}
                        </select>
                    </label>
                    {block.note && <p className={styles.blockNote}>{block.note}</p>}
                    <div className={styles.charGrid}>
                        {this.blockChars.map(c => (
                            <button key={c.cp} className={styles.charCell}
                                onClick={() => this.lookupCp('U+' + c.cp.toString(16).toUpperCase())}
                                title={`U+${c.cp.toString(16).toUpperCase().padStart(4, '0')}`}>
                                <span className={styles.charGlyph}>{c.char}</span>
                                <span className={styles.charCode}>{c.cp.toString(16).toUpperCase().padStart(4, '0')}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
}
